import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { Loader2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export interface ConfirmationDialogProps {
  title: string;
  description: ReactNode;
  /**
   * Sync handlers close the dialog immediately. When a promise is returned the confirm button
   * shows a spinner, both buttons disable, and the dialog stays open until it settles: it closes
   * on resolve and stays open on reject (surface the error from inside `onConfirm`).
   */
  onConfirm: () => void | Promise<unknown>;
  children?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: "default" | "destructive";
}

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { then?: unknown }).then === "function"
  );
}

export function ConfirmationDialog({
  title,
  description,
  onConfirm,
  children,
  open,
  onOpenChange,
  disabled,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmVariant = "default",
}: ConfirmationDialogProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const mountedRef = useRef(true);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : uncontrolledOpen;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setOpen = (next: boolean) => {
    if (!isControlled) {
      setUncontrolledOpen(next);
    }
    onOpenChange?.(next);
  };

  const handleOpenChange = (next: boolean) => {
    // Escape / Cancel cannot dismiss while the confirm action is still running.
    if (!next && pending) {
      return;
    }
    setOpen(next);
  };

  const handleConfirm = (event: MouseEvent<HTMLButtonElement>) => {
    // Radix closes on Action click unless prevented; closing is driven by the result below.
    event.preventDefault();
    if (pending) {
      return;
    }
    const result = onConfirm();
    if (!isPromiseLike(result)) {
      setOpen(false);
      return;
    }
    setPending(true);
    Promise.resolve(result).then(
      () => {
        if (!mountedRef.current) return;
        setPending(false);
        setOpen(false);
      },
      () => {
        if (!mountedRef.current) return;
        setPending(false);
      }
    );
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      {children ? <AlertDialogTrigger asChild>{children}</AlertDialogTrigger> : null}
      <AlertDialogContent aria-busy={pending || undefined}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {typeof description === "string" ? (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          ) : (
            <AlertDialogDescription asChild>
              <div>{description}</div>
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            variant={confirmVariant}
            onClick={handleConfirm}
            disabled={disabled || pending}
          >
            {pending ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
